import styled from "styled-components";

/* ============================================================
   Sacred geometry SVGs — mandala, vesica piscis, golden spiral,
   rose window. Pure SVG, animated via CSS. Decorative layers.
============================================================ */

const Svg = styled.svg`
  display: block;
  overflow: visible;
`;

/* ---- Mandala: concentric petal rings (hero orb backdrop) ---- */
export function Mandala({ size = 620, stroke = "#C9A84C", opacity = 0.5 }) {
  const cx = size / 2;
  const petals = (count, r, rot = 0) =>
    Array.from({ length: count }).map((_, i) => {
      const a = (360 / count) * i + rot;
      return (
        <ellipse
          key={`${count}-${i}`}
          cx={cx}
          cy={cx - r / 2}
          rx={r * 0.16}
          ry={r * 0.5}
          transform={`rotate(${a} ${cx} ${cx})`}
        />
      );
    });
  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ opacity }}>
      <g fill="none" stroke={stroke} strokeWidth="0.8">
        {[260, 200, 140, 90].map((r) => (
          <circle key={r} cx={cx} cy={cx} r={r} />
        ))}
        <g opacity="0.85">{petals(24, 230, 0)}</g>
        <g opacity="0.7">{petals(16, 170, 11)}</g>
        <g opacity="0.6">{petals(12, 110, 0)}</g>
      </g>
    </Svg>
  );
}

/* ---- Vesica Piscis flower-of-life cluster ---- */
export function Vesica({ size = 200, stroke = "#6B3FA0", opacity = 0.4 }) {
  const c = size / 2;
  const r = size / 4;
  const pts = [
    [c, c],
    [c + r, c],
    [c - r, c],
    [c + r / 2, c - r * 0.86],
    [c - r / 2, c - r * 0.86],
    [c + r / 2, c + r * 0.86],
    [c - r / 2, c + r * 0.86],
  ];
  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ opacity }}>
      <g fill="none" stroke={stroke} strokeWidth="0.7">
        {pts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={r} />
        ))}
      </g>
    </Svg>
  );
}

/* ---- Golden ratio spiral ---- */
export function GoldenSpiral({ size = 320, stroke = "#C9A84C", opacity = 0.35 }) {
  // Fibonacci-arc approximation
  const path =
    "M310 10 A300 300 0 0 1 10 310 A185 185 0 0 1 195 125 A114 114 0 0 1 309 239 " +
    "A70 70 0 0 1 239 309 A43 43 0 0 1 196 266 A27 27 0 0 1 223 239";
  return (
    <Svg width={size} height={size} viewBox="0 0 320 320" style={{ opacity }}>
      <path d={path} fill="none" stroke={stroke} strokeWidth="0.8" />
    </Svg>
  );
}

/* ---- Rose window (cathedral) — radial tracery for section dividers ---- */
export function RoseWindow({ size = 160, stroke = "#C9A84C", opacity = 0.5 }) {
  const c = size / 2;
  const ring = (count, r, rr) =>
    Array.from({ length: count }).map((_, i) => {
      const a = ((2 * Math.PI) / count) * i;
      return (
        <circle key={`${count}-${i}`} cx={c + Math.cos(a) * r} cy={c + Math.sin(a) * r} r={rr} />
      );
    });
  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ opacity }}>
      <g fill="none" stroke={stroke} strokeWidth="0.7">
        <circle cx={c} cy={c} r={c - 6} />
        <circle cx={c} cy={c} r={(c - 6) * 0.62} />
        <circle cx={c} cy={c} r={(c - 6) * 0.28} />
        {ring(8, (c - 6) * 0.8, (c - 6) * 0.18)}
        {ring(8, (c - 6) * 0.45, (c - 6) * 0.12)}
      </g>
    </Svg>
  );
}

/* small inline cross/feather motif for card embossing */
export function CrossMotif({ size = 120, color = "#C9A84C", opacity = 0.08 }) {
  const c = size / 2;
  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ opacity }}>
      <g fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round">
        <line x1={c} y1="14" x2={c} y2={size - 14} />
        <line x1="26" y1={c * 0.85} x2={size - 26} y2={c * 0.85} />
        <circle cx={c} cy={c * 0.85} r="5" />
      </g>
    </Svg>
  );
}
